import { ExternalLink, Film, ImageIcon, Link2 } from "lucide-react";

const IMAGE_RE = /\.(png|jpe?g|gif|webp|avif|svg)(\?.*)?$/i;
const VIDEO_RE = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i;

const mediaKind = (url) => {
  if (!url) return "none";
  if (IMAGE_RE.test(url)) return "image";
  if (VIDEO_RE.test(url)) return "video";
  return "link";
};

const Thumb = ({ url, kind }) => {
  if (kind === "image") {
    return (
      <img
        src={url}
        alt="Creative preview"
        className="h-full w-full object-contain"
        loading="lazy"
      />
    );
  }
  if (kind === "video") {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="h-full w-full object-contain"
      />
    );
  }
  const Icon = kind === "image" ? ImageIcon : Film;
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-(--text-faint)">
      {kind === "link" ? (
        <Link2 size={22} strokeWidth={1.8} />
      ) : (
        <Icon size={22} strokeWidth={1.8} />
      )}
      <span className="text-[11px] font-medium">No inline preview</span>
    </div>
  );
};

export default function CreativePreview({
  url,
  width,
  height,
  durationSeconds,
  name,
  compact = false,
}) {
  if (!url) return null;
  const kind = mediaKind(url);
  const size =
    width && height ? `${width} × ${height} px` : width || height ? "—" : "—";
  const duration =
    durationSeconds != null && durationSeconds !== ""
      ? `${durationSeconds}s`
      : "—";

  return (
    <div className="flex gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) p-3">
      <div
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-1) ${
          compact ? "h-[54px] w-24" : "h-[200px] w-80"
        }`}
      >
        <Thumb url={url} kind={kind} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
        {name && (
          <p className="truncate text-[13px] font-semibold text-(--text-primary)">
            {name}
          </p>
        )}
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
          <div className="flex items-center gap-1.5">
            <dt className="text-(--text-muted)">Size</dt>
            <dd className="font-semibold text-(--text-primary)">{size}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="text-(--text-muted)">Length</dt>
            <dd className="font-semibold text-(--text-primary)">{duration}</dd>
          </div>
        </dl>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1 text-[12px] font-medium text-(--brand-primary) hover:underline"
          title={url}
        >
          <ExternalLink size={12} strokeWidth={2} className="shrink-0" />
          <span className="truncate">{url}</span>
        </a>
      </div>
    </div>
  );
}
